/** Tile coordinates and the neighbour order every rule follows. */

export interface Coord {
  readonly x: number;
  readonly y: number;
}

/**
 * Coordinates are valid when -limit < x, y < limit. The official limit is COORD_LIMIT; a test
 * configuration may lower it so that vectors can reach the edge by walking (core spec 4.1).
 */
export const COORD_LIMIT = 1048576; // 2^20

export function isValidCoord(x: number, y: number, limit: number = COORD_LIMIT): boolean {
  return (
    Number.isInteger(x) && Number.isInteger(y) && x > -limit && x < limit && y > -limit && y < limit
  );
}

/**
 * The eight neighbours in the order every rule visits them: row above left to right,
 * then the same row, then the row below. Never reorder; the flood fill and chording
 * depend on it and the golden vectors pin it.
 */
export const NEIGHBOURS: readonly (readonly [number, number])[] = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
];

const KEY_OFFSET = COORD_LIMIT;
const KEY_SPAN = COORD_LIMIT * 2;

/** Packs a coordinate into one exact integer key for sets and maps. Internal to this package. */
export function tileKey(x: number, y: number): number {
  return (x + KEY_OFFSET) * KEY_SPAN + (y + KEY_OFFSET);
}

/** Inverse of tileKey. */
export function keyToCoord(key: number): Coord {
  const x = Math.floor(key / KEY_SPAN) - KEY_OFFSET;
  const y = (key % KEY_SPAN) - KEY_OFFSET;
  return { x, y };
}

/** Squared Euclidean distance from the cabin at the origin. */
export function distanceSq(x: number, y: number): number {
  return x * x + y * y;
}
